import { createApp, h, type App } from 'vue'
import CrmNoOrderPage from '../components/crm/CrmNoOrderPage.vue'
import { readNoOrderReceipt, saveNoOrderReceipt } from '../lib/crm-no-order-receipt'
import type { NoOrderRegistrationInput, NoOrderRegistrationResult } from '../../shared/crm-no-order-needed.js'
import type { CrmEmbedClient } from './crm-embed-client'

const SUCCESS_PARAM = 'crmWarranty'
export const isCrmEmbedNoOrderSuccess = () => new URL(location.href).searchParams.get(SUCCESS_PARAM) === 'success'

// Render the existing page inside the host component, with an authenticated
// embed transport. No iframe, host-route installation or duplicate mail logic.
export class CrmEmbedNoOrderFlow {
  private app: App | null = null
  private version = 0
  private disposed = false
  private onPopState = () => { void this.load() }

  private root: ShadowRoot
  private host: HTMLElement
  private client: CrmEmbedClient
  private siteId: string

  constructor(root: ShadowRoot, host: HTMLElement, client: CrmEmbedClient, siteId: string) {
    this.root = root
    this.host = host
    this.client = client
    this.siteId = siteId
  }

  async start() {
    window.addEventListener('popstate', this.onPopState)
    await this.load()
  }

  destroy() {
    this.disposed = true
    this.version++
    window.removeEventListener('popstate', this.onPopState)
    this.clear()
  }

  private clear() {
    this.app?.unmount()
    this.app = null
    this.root.replaceChildren()
  }

  private async load() {
    const version = ++this.version
    try {
      const success = isCrmEmbedNoOrderSuccess()
      const receipt = success ? readNoOrderReceipt(this.siteId) : null
      if (success && !receipt) throw new Error('Please register your warranty first.')
      const payload = await this.client.getNoOrderPage(receipt?.token)
      if (success && !payload.registered) throw new Error('Please register your warranty first.')
      if (this.disposed || version !== this.version) return
      this.clear()
      const style = document.createElement('style')
      style.textContent = (CrmNoOrderPage as typeof CrmNoOrderPage & { styles?: string[] }).styles?.join('\n') || ''
      const container = document.createElement('div')
      this.root.append(style, container)
      this.app = createApp({ render: () => h(CrmNoOrderPage, {
        content: payload.pageContent, brandName: payload.brandName, siteId: this.siteId,
        siteTheme: payload.siteTheme, success, emailStatus: payload.emailStatus || '',
        alreadyRegistered: Boolean(receipt?.alreadyRegistered),
        register: (input: NoOrderRegistrationInput) => this.client.registerNoOrder(input),
        onCompleted: (result: Omit<NoOrderRegistrationResult, 'registered'>) => this.completed(result),
      }) })
      this.app.mount(container)
    } catch (error) {
      if (this.disposed || version !== this.version) return
      this.clear()
      const section = document.createElement('section')
      section.style.cssText = 'padding:48px 24px;font-family:Arial,sans-serif;text-align:center'
      section.setAttribute('role', 'alert')
      const message = document.createElement('p')
      message.textContent = error instanceof Error ? error.message : 'This registration page is unavailable.'
      const retry = document.createElement('button')
      retry.type = 'button'
      retry.textContent = 'Try Again'
      retry.onclick = () => { void this.load() }
      section.append(message, retry)
      if (isCrmEmbedNoOrderSuccess()) {
        const back = document.createElement('button')
        back.type = 'button'
        back.textContent = 'Return to warranty registration'
        back.onclick = () => {
          const url = new URL(location.href)
          url.searchParams.delete(SUCCESS_PARAM)
          window.history.pushState(window.history.state, '', url)
          void this.load()
        }
        section.append(back)
      }
      this.root.append(section)
    }
  }

  private completed(result: Omit<NoOrderRegistrationResult, 'registered'>) {
    if (this.disposed) return
    const receipt = { token: result.receiptToken, alreadyRegistered: result.alreadyRegistered }
    saveNoOrderReceipt(this.siteId, receipt)
    const url = new URL(location.href)
    url.searchParams.set(SUCCESS_PARAM, 'success')
    // Retain the external site's real /warranty route so reload never requests
    // a /warranty/success route which the customer's site may not implement.
    window.history.pushState({ ...window.history.state, noOrderSiteId: this.siteId, noOrderReceipt: receipt }, '', url)
    void this.load()
    this.host.scrollIntoView({ block: 'start' })
    this.host.dispatchEvent(new CustomEvent('crm-flow-complete', { bubbles: true, composed: true,
      detail: { flowType: 'warranty', servicePageType: 'no_order_needed', stageKey: 'COMPLETED' } }))
  }
}
